import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SidebarLayout.css';

const SidebarLayout = ({ menuItems, subtitle, logoIcon = '🧘', logoText = 'Tu Masaje' }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cerrar sidebar en móvil al cambiar de ruta
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const getInitials = () => {
        if (!user) return '?';
        const n = user.nombre?.trim();
        const a = user.apellido?.trim();
        if (n && a) return `${n.charAt(0)}${a.charAt(0)}`.toUpperCase();
        if (n) return n.charAt(0).toUpperCase();
        if (user.email) return user.email.charAt(0).toUpperCase();
        return '?';
    };

    const getNombreCompleto = () => {
        if (!user) return '';
        const nombre = [user.nombre, user.apellido].filter(Boolean).join(' ').trim();
        return nombre || user.email;
    };

    const getRolLabel = () => {
        if (!user) return '';
        const roles = { admin: 'Administrador', masajista: 'Masajista', paciente: 'Paciente' };
        return roles[user.role] || user.role;
    };

    const getPageTitle = () => {
        const found = menuItems.find(item => location.pathname.startsWith(item.path));
        return found?.label || 'Panel';
    };

    return (
        <div className="sidebar-layout">
            {/* Botón hamburguesa móvil */}
            <button
                className="sidebar-mobile-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Abrir menú"
            >
                <span className={`sidebar-toggle-icon ${sidebarOpen ? 'open' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                </span>
            </button>

            {/* Overlay móvil */}
            {sidebarOpen && (
                <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <Link to="/" className="sidebar-logo">
                        <span className="sidebar-logo-icon">{logoIcon}</span>
                        <span className="sidebar-logo-text">{logoText}</span>
                    </Link>
                    <div className="sidebar-subtitle">{subtitle}</div>
                </div>

                <nav className="sidebar-nav">
                    <ul className="sidebar-menu">
                        {menuItems.map((item) => {
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <li key={item.path} className="sidebar-menu-item">
                                    <Link
                                        to={item.path}
                                        className={`sidebar-menu-link ${isActive ? 'active' : ''}`}
                                    >
                                        <span className="menu-icon">{item.icon}</span>
                                        <div className="menu-content">
                                            <span className="menu-label">{item.label}</span>
                                            <span className="menu-description">{item.description}</span>
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                <div className="sidebar-footer" ref={dropdownRef}>
                    {menuOpen && (
                        <div className="sidebar-user-dropdown">
                            <Link
                                to="/perfil"
                                state={{ from: location.pathname + location.search }}
                                className="sidebar-dropdown-item"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="dropdown-item-icon">👤</span>
                                Mi Perfil
                            </Link>
                            <Link
                                to="/perfil?tab=password"
                                state={{ from: location.pathname + location.search }}
                                className="sidebar-dropdown-item"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="dropdown-item-icon">🔑</span>
                                Cambiar Contraseña
                            </Link>
                            <div className="sidebar-dropdown-divider"></div>
                            <button
                                className="sidebar-dropdown-item sidebar-dropdown-logout"
                                onClick={handleLogout}
                            >
                                <span className="dropdown-item-icon">🚪</span>
                                Cerrar Sesión
                            </button>
                        </div>
                    )}

                    <button
                        className="sidebar-user-btn"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-expanded={menuOpen}
                    >
                        <div className="sidebar-user-avatar">{getInitials()}</div>
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name">{getNombreCompleto()}</div>
                            <div className="sidebar-user-role">{getRolLabel()}</div>
                        </div>
                        <span className={`sidebar-user-caret ${menuOpen ? 'open' : ''}`}>▴</span>
                    </button>
                </div>
            </aside>

            {/* Contenido */}
            <main className="sidebar-main-content">
                <div className="sidebar-content-header">
                    <div className="sidebar-breadcrumb">
                        <span className="breadcrumb-item">Panel</span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-item active">{getPageTitle()}</span>
                    </div>
                </div>
                <div className="sidebar-content-body">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default SidebarLayout;